import {
  TableCell as MUITableCell,
  TableHead as MUITableHead,
  TableRow as MUITableRow,
  makeStyles,
} from "@material-ui/core";
import classNames from "classnames";
import type { StylingProps, TableColumnDefinition } from "./types";
import type { ColumnTotals } from "./util";
import {
  computeColumnWidth,
  mapAlignmentToTextAlign,
  mapAlignmentToVerticalAlign,
} from "./util";

const useStyles = makeStyles((theme) => ({
  headerRow: {
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
  },
  headerCell: {
    minHeight: 36,
    boxSizing: "border-box",
    padding: theme.spacing(0, 2),
    textTransform: "uppercase",
    fontWeight: 700,
    fontSize: "12px",
    color: theme.palette.grey[600],
    borderBottom: "revert",
  },
  cell: {
    "&:first-child": {
      paddingLeft: theme.spacing(),
    },
    "&:last-child": {
      paddingRight: theme.spacing(),
    },
  },
  stickyCell: {
    position: "sticky",
    left: 0,
    zIndex: 100,
    // Header row currently has no explicit background, so keep an explicit
    // paper colour here — the body sticky cell inherits the row instead.
    background: theme.palette.background.paper,
  },
  lastStickyCell: {
    boxShadow: "var(--sticky-shadow, none)",
    clipPath: "inset(0 -10px 0 0)",
  },
}));

interface TableHeaderProps {
  columns: TableColumnDefinition[];
  classes?: StylingProps;
  minTableWidth?: number;
  columnTotals: ColumnTotals;
}

const TableHeader = ({
  columns,
  classes,
  minTableWidth,
  columnTotals,
}: TableHeaderProps) => {
  const headerClasses = useStyles();

  const getStickyLeft = (cellIndex: number) => {
    let left = 0;
    for (let i = 0; i < cellIndex; i++) {
      if (columns[i].isSticky) {
        left += columns[i].width || 0;
      }
    }
    return left;
  };

  const isLastSticky = (idx: number) => {
    return (
      columns[idx].isSticky &&
      (idx === columns.length - 1 || !columns[idx + 1].isSticky)
    );
  };

  return (
    <MUITableHead>
      <MUITableRow className={headerClasses.headerRow}>
        {columns.map((column, idx) => (
          <MUITableCell
            key={`${column.field}-header`}
            align="left"
            className={classNames(
              headerClasses.headerCell,
              headerClasses.cell,
              classes?.headerCellClass,
              column.isSticky && headerClasses.stickyCell,
              isLastSticky(idx) && headerClasses.lastStickyCell,
            )}
            style={{
              textAlign: mapAlignmentToTextAlign(column.alignContents),
              verticalAlign: mapAlignmentToVerticalAlign(
                column.verticalAlignContents,
              ),
              width: computeColumnWidth(column, columnTotals, minTableWidth),
              left: column.isSticky ? getStickyLeft(idx) : "auto",
            }}
          >
            {column.headerName}
          </MUITableCell>
        ))}
      </MUITableRow>
    </MUITableHead>
  );
};

TableHeader.defaultProps = {
  classes: null,
};

export default TableHeader;
